import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API } from '../utils/api';
import { 
  Search, MapPin, Phone, MessageCircle, Users, Clock, 
  Video, ExternalLink, Loader2, Heart, Globe, Wine, Pill
} from 'lucide-react';


function WhatsAppButton({ number, groupName, organization }) {
  if (!number) return null;
  var cleanNumber = number.replace(/\D/g, '');
  if (!cleanNumber.startsWith('56')) cleanNumber = '56' + cleanNumber;
  var orgName = organization === 'aa' ? 'Alcohólicos Anónimos' : 'Narcóticos Anónimos';
  var message = encodeURIComponent('Hola, los contacto desde sinadicciones.org. Estoy interesado en el grupo "' + groupName + '" de ' + orgName + '. ¿Podrían darme más información?');
  var url = 'https://wa.me/' + cleanNumber + '?text=' + message;
  
  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/40 rounded-lg hover:bg-green-500/30 transition-colors text-green-400 text-sm font-medium"
      data-testid="group-whatsapp-btn"
    >
      <MessageCircle className="w-4 h-4" />
      <span>WhatsApp</span>
    </a>
  );
}

function GoogleMapsLink({ address, city }) {
  if (!address) return null;
  var query = encodeURIComponent(address + ', ' + (city || '') + ', Chile');
  var url = 'https://www.google.com/maps/search/?api=1&query=' + query;
  
  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
      data-testid="group-maps-link"
    >
      <MapPin className="w-4 h-4" />
      <span>Ver en mapa</span>
    </a>
  );
}

function ZoomLink({ zoomId, zoomPassword }) {
  if (!zoomId) return null;
  return (
    <div className="flex items-center gap-2 text-blue-400 text-sm">
      <Video className="w-4 h-4" />
      <span>Zoom ID: {zoomId}</span>
      {zoomPassword && <span className="text-slate-400">| Clave: {zoomPassword}</span>}
    </div>
  );
}

function MeetLink({ meetId }) {
  if (!meetId) return null;
  var url = 'https://meet.google.com/' + meetId;
  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
    >
      <Video className="w-4 h-4" />
      <span>Google Meet: {meetId}</span>
      <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function GroupCard({ group, organization }) {
  var isOnline = group.type === 'online';
  var isMixto = group.type === 'mixto';
  
  return (
    <Card className="bg-slate-800/50 border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300" data-testid="support-group-card">
      <CardContent className="p-5">
        <div className="flex flex-col gap-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white" data-testid="group-name">{group.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={
                  'px-2 py-0.5 rounded text-xs font-medium ' +
                  (isOnline ? 'bg-blue-500/20 text-blue-400' : 
                   isMixto ? 'bg-purple-500/20 text-purple-400' : 
                   'bg-emerald-500/20 text-emerald-400')
                }>
                  {isOnline ? 'Online' : isMixto ? 'Mixto' : 'Presencial'}
                </span>
                <span className="text-sm text-slate-400">{group.city}</span>
              </div>
            </div>
            {group.whatsapp && <WhatsAppButton number={group.whatsapp} groupName={group.name} organization={organization} />}
          </div>
          
          {/* Details */}
          <div className="space-y-2 text-sm">
            {/* Address */}
            {!isOnline && group.address && (
              <div className="flex items-start gap-2 text-slate-300">
                <MapPin className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
                <span>{group.address}</span>
              </div>
            )}
            
            {/* Schedule */}
            <div className="flex items-start gap-2 text-slate-300">
              <Clock className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
              <span>{group.schedule}</span>
            </div>
            
            {/* Contact name */}
            {group.contact_name && (
              <div className="flex items-center gap-2 text-slate-400">
                <Users className="w-4 h-4 text-slate-500" />
                <span>Contacto: {group.contact_name}</span>
              </div>
            )}
            
            {/* Online meeting info */}
            {(group.zoom_id || group.meet_id) && (
              <div className="pt-2 border-t border-slate-700/50">
                {group.zoom_id && <ZoomLink zoomId={group.zoom_id} zoomPassword={group.zoom_password} />}
                {group.meet_id && <MeetLink meetId={group.meet_id} />}
              </div>
            )}
          </div>
          
          {/* Footer links */}
          {!isOnline && (group.maps_url || group.address) && (
            <div className="pt-2 border-t border-slate-700/50 flex items-center gap-4">
              {group.maps_url ? (
                <a 
                  href={group.maps_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
                >
                  <MapPin className="w-4 h-4" />
                  <span>Ver en mapa</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <GoogleMapsLink address={group.address} city={group.city} />
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function AAGroupsPage() {
  var [activeTab, setActiveTab] = useState('aa');
  var [groups, setGroups] = useState([]);
  var [regions, setRegions] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedRegion, setSelectedRegion] = useState('all');
  var [selectedType, setSelectedType] = useState('all');
  
  useEffect(function() {
    loadGroups();
  }, [activeTab, selectedRegion, selectedType]);
  
  async function loadGroups() {
    setLoading(true);
    try {
      var params = new URLSearchParams();
      if (selectedRegion && selectedRegion !== 'all') params.append('region', selectedRegion);
      if (selectedType && selectedType !== 'all') params.append('group_type', selectedType);
      
      var endpoint = activeTab === 'aa' ? '/aa-groups/public' : '/na-groups/public';
      var response = await axios.get(API + endpoint + '?' + params.toString());
      setGroups(response.data.groups || []);
      setRegions(response.data.regions || []);
    } catch (e) {
      console.error('Error loading groups:', e);
    } finally {
      setLoading(false);
    }
  }
  
  function handleTabChange(tab) {
    setActiveTab(tab);
    setSelectedRegion('all');
    setSelectedType('all');
  }
  
  // Group by region for display
  var groupedByRegion = {};
  groups.forEach(function(group) {
    if (!groupedByRegion[group.region]) {
      groupedByRegion[group.region] = [];
    }
    groupedByRegion[group.region].push(group);
  });
  
  // Sort regions (Online at the end)
  var sortedRegions = Object.keys(groupedByRegion).sort(function(a, b) {
    if (a === 'Online') return 1;
    if (b === 'Online') return -1;
    return a.localeCompare(b);
  });
  
  var pageTitle = activeTab === 'aa' ? 'Alcohólicos Anónimos' : 'Narcóticos Anónimos';
  var pageDescription = activeTab === 'aa' 
    ? 'Encuentra grupos de Alcohólicos Anónimos (AA) cerca de ti en Chile.'
    : 'Encuentra grupos de Narcóticos Anónimos (NA) cerca de ti en Chile.';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800">
      
        <title>Grupos de Apoyo AA y NA en Chile 2026 | Alcohólicos y Narcóticos Anónimos | Sin Adicciones</title>
        <meta name="description" content="Encuentra grupos de Alcohólicos Anónimos (AA) y Narcóticos Anónimos (NA) cerca de ti en Chile. Directorio completo con horarios, direcciones y contactos gratuitos." />
        <meta name="keywords" content="alcohólicos anónimos Chile, AA Chile, narcóticos anónimos, NA Chile, grupos apoyo adicciones, reuniones AA" />
        <link rel="canonical" href="https://sinadicciones.org/grupos-aa" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Grupos de Apoyo AA y NA en Chile | Sin Adicciones" />
        <meta property="og:description" content="Directorio de grupos de Alcohólicos Anónimos y Narcóticos Anónimos en Chile. Encuentra reuniones cerca de ti." />
        <meta property="og:url" content="https://sinadicciones.org/grupos-aa" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Grupos de Apoyo AA y NA en Chile" />
        <meta name="twitter:description" content="Encuentra reuniones de AA y NA cerca de ti en Chile." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      
      
      <Header />
      
      <main className="container mx-auto px-4 py-4 md:py-8 pt-20 md:pt-24">
        {/* Hero Section - Compact on mobile */}
        <div className="text-center mb-4 md:mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-2 md:mb-4">
            <Heart className="w-4 md:w-5 h-4 md:h-5 text-blue-400" />
            <span className="text-blue-400 font-medium text-sm md:text-base">Grupos de Apoyo</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4">
            Grupos de Apoyo en Chile
          </h1>
          <p className="text-sm md:text-base text-slate-400 max-w-2xl mx-auto hidden md:block">
            Encuentra un grupo de apoyo cerca de ti. Reuniones presenciales y online disponibles en todo Chile.
            El primer paso hacia la recuperación es pedir ayuda.
          </p>
        </div>
        
        {/* Tabs - Compact on mobile */}
        <div className="flex justify-center mb-4 md:mb-8">
          <div className="inline-flex bg-slate-800/50 border border-slate-700/50 rounded-xl p-1">
            <button
              onClick={function() { handleTabChange('aa'); }}
              className={
                'flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all text-sm md:text-base ' +
                (activeTab === 'aa' 
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                  : 'text-slate-400 hover:text-white')
              }
              data-testid="tab-aa"
            >
              <Wine className="w-4 md:w-5 h-4 md:h-5" />
              <span className="hidden sm:inline">Alcohólicos Anónimos</span>
              <span className="sm:hidden">AA</span>
            </button>
            <button
              onClick={function() { handleTabChange('na'); }}
              className={
                'flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all text-sm md:text-base ' +
                (activeTab === 'na' 
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                  : 'text-slate-400 hover:text-white')
              }
              data-testid="tab-na"
            >
              <Pill className="w-4 md:w-5 h-4 md:h-5" />
              <span className="hidden sm:inline">Narcóticos Anónimos</span>
              <span className="sm:hidden">NA</span>
            </button>
          </div>
        </div>
        
        {/* Filters - Compact on mobile */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 md:p-4 mb-4 md:mb-8">
          <div className="flex flex-col sm:flex-row gap-2 md:gap-4">
            <div className="flex-1">
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger className="bg-slate-900/50 border-slate-700 h-10 text-sm" data-testid="region-filter">
                  <SelectValue placeholder="Todas las regiones" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">Todas las regiones</SelectItem>
                  {regions.map(function(region) {
                    return (
                      <SelectItem key={region} value={region}>{region}</SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="bg-slate-900/50 border-slate-700 h-10 text-sm" data-testid="type-filter">
                  <SelectValue placeholder="Todas las modalidades" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">Todas las modalidades</SelectItem>
                  <SelectItem value="presencial">Presencial</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="mixto">Mixto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-center sm:justify-end">
              <div className={
                'border rounded-lg px-3 py-1.5 md:px-4 md:py-2 ' +
                (activeTab === 'aa' 
                  ? 'bg-amber-500/10 border-amber-500/30' 
                  : 'bg-purple-500/10 border-purple-500/30')
              }>
                <span className={
                  'font-semibold text-base md:text-lg ' +
                  (activeTab === 'aa' ? 'text-amber-400' : 'text-purple-400')
                }>{groups.length}</span>
                <span className="text-slate-400 text-xs md:text-sm ml-1 md:ml-2">grupos</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Results */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No se encontraron grupos</h3>
            <p className="text-slate-400">Intenta con otros filtros o busca en otra región</p>
          </div>
        ) : (
          <div className="space-y-8">
            {sortedRegions.map(function(region) {
              var regionGroups = groupedByRegion[region];
              return (
                <div key={region}>
                  <div className="flex items-center gap-3 mb-4">
                    {region === 'Online' ? (
                      <Globe className="w-5 h-5 text-blue-400" />
                    ) : (
                      <MapPin className={
                        'w-5 h-5 ' + (activeTab === 'aa' ? 'text-amber-400' : 'text-purple-400')
                      } />
                    )}
                    <h2 className="text-xl font-semibold text-white">{region}</h2>
                    <span className="text-sm text-slate-500">({regionGroups.length} grupos)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {regionGroups.map(function(group) {
                      return <GroupCard key={group.group_id} group={group} organization={activeTab} />;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Info Section */}
        <div className="mt-12 bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-3">
            {activeTab === 'aa' ? '¿Qué es Alcohólicos Anónimos?' : '¿Qué es Narcóticos Anónimos?'}
          </h3>
          <p className="text-slate-400 mb-4">
            {activeTab === 'aa' 
              ? 'Alcohólicos Anónimos es una comunidad de hombres y mujeres que comparten su experiencia, fortaleza y esperanza para resolver su problema común y ayudar a otros a recuperarse del alcoholismo.'
              : 'Narcóticos Anónimos es una confraternidad sin ánimo de lucro compuesta por hombres y mujeres para quienes las drogas se habían convertido en un problema grave. Somos adictos en recuperación que nos reunimos para ayudarnos a permanecer limpios.'}
          </p>
          <div className="flex flex-wrap gap-4">
            <a 
              href={activeTab === 'aa' ? 'https://www.alcoholicosanonimos.cl' : 'https://nachile.cl'} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300"
            >
              <Globe className="w-4 h-4" />
              <span>Sitio oficial {activeTab === 'aa' ? 'AA' : 'NA'} Chile</span>
              <ExternalLink className="w-3 h-3" />
            </a>
            {activeTab === 'na' && (
              <a 
                href="https://wa.me/56927512418" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-green-400 hover:text-green-300"
              >
                <Phone className="w-4 h-4" />
                <span>Fono Ayuda NA: +56 9 2751 2418</span>
              </a>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default AAGroupsPage;
